import { Button, message, Spin, Tag, Tooltip } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PatchNoteMarkdown } from "./PatchNoteMarkdown";
import {
  fetchPatchNoteRelease,
  getPatchNoteReleasePath,
  getPatchNoteReleaseUrl,
  normalizePatchNoteChannel,
  PatchNoteRelease,
  ReleaseChannel,
} from "../utils/patchNotes";

const CHANNEL_TAG_CLASS: Partial<Record<ReleaseChannel, string>> = {
  alpha: "patch-notes-tag-channel-alpha",
  beta: "patch-notes-tag-channel-beta",
};

type ReleaseHeaderProps = {
  release: PatchNoteRelease;
  showCopyLink?: boolean;
  titleAsLink?: boolean;
};

export const ReleaseHeader = ({
  release,
  showCopyLink = false,
  titleAsLink = false,
}: ReleaseHeaderProps) => {
  const normalizedChannel = normalizePatchNoteChannel(release.channel);

  const copyLink = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    navigator.clipboard
      .writeText(getPatchNoteReleaseUrl(release.id))
      .then(() => message.success("Link copied to clipboard"))
      .catch(() => message.error("Failed to copy link"));
  };

  const title = (
    <span className="patch-notes-item-title">{release.title}</span>
  );

  return (
    <div className="patch-notes-item-header">
      <div className="patch-notes-item-title-row">
        <div className="patch-notes-item-title-group">
          {titleAsLink ? (
            <Link
              to={getPatchNoteReleasePath(release.id)}
              className="patch-notes-item-title-link"
              onClick={(event) => event.stopPropagation()}
            >
              {title}
            </Link>
          ) : (
            title
          )}
          {showCopyLink && (
            <Tooltip title="Copy link to this release">
              <Button
                type="text"
                size="small"
                className="patch-notes-copy-link"
                icon={<CopyOutlined />}
                onClick={copyLink}
                aria-label="Copy link to this release"
              />
            </Tooltip>
          )}
        </div>
        <span className="patch-notes-date">
          {new Date(release.date).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </span>
      </div>
      <div className="patch-notes-badges">
        {release.launcherVersion && (
          <Tag className="patch-notes-tag-launcher">
            Launcher {release.launcherVersion}
          </Tag>
        )}
        {release.gamesVersion && (
          <Tag className="patch-notes-tag-games">
            Games {release.gamesVersion}
          </Tag>
        )}
        {normalizedChannel !== "latest" && (
          <Tag className={CHANNEL_TAG_CLASS[normalizedChannel]}>
            {normalizedChannel}
          </Tag>
        )}
      </div>
    </div>
  );
};

type PatchNoteReleaseDetailProps = {
  release: PatchNoteRelease;
  showHeader?: boolean;
  showCopyLink?: boolean;
};

export const PatchNoteReleaseDetail = ({
  release,
  showHeader = true,
  showCopyLink = true,
}: PatchNoteReleaseDetailProps) => {
  const [content, setContent] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const markdown = await fetchPatchNoteRelease(release.file);
        if (!cancelled) setContent(markdown);
      } catch {
        if (!cancelled) setError("Failed to load release notes.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [release.file]);

  return (
    <div className="patch-notes-release-detail">
      {showHeader && (
        <ReleaseHeader release={release} showCopyLink={showCopyLink} />
      )}
      {loading ? (
        <div className="patch-notes-loading-inline">
          <Spin />
        </div>
      ) : error ? (
        <p className="patch-notes-error">{error}</p>
      ) : (
        <PatchNoteMarkdown content={content || ""} />
      )}
    </div>
  );
};
